import {useCallback, useEffect, useRef, useState} from "react";
import {CURRENCIES} from "../curify/constants";

const FLEXECOM_ENVIRONMENT = "development";

const DEFAULT_BILLING_ADDRESS = {
  line_1: "123 Example Rd.",
  city: "New York",
  state: "New York",
  postal_code: "10001",
  country_code: "US",
};

const CARD_FIELDS_STYLE = {
  fonts: ["https://fonts.googleapis.com/css?family=Poppins:300,400,500,600,700"],
  styles: {
    body: { fontFamily: "Poppins, sans-serif" },
    label: { display: "none" },
    "[ev-component=card]": {
      display: "grid",
      gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)",
      gap: "8px",
    },
    ".field[ev-name=name]": {
      gridColumn: "1 / -1",
      gridRow: "1",
      minWidth: 0,
    },
    ".field[ev-name=number]": {
      gridColumn: "1",
      gridRow: "2",
      minWidth: 0,
    },
    ".field[ev-name=expiry]": {
      gridColumn: "2",
      gridRow: "2",
      minWidth: 0,
    },
    ".field[ev-name=cvc]": {
      gridColumn: "3",
      gridRow: "2",
      minWidth: 0,
    },
    input: {
      boxSizing: "border-box",
      width: "100%",
      fontFamily: "Poppins, sans-serif",
      fontSize: 14,
      height: 56,
      padding: "0 16px",
      borderRadius: 6,
      color: "#3f4254",
      border: "1px solid #6f55ff",
      backgroundColor: "#6f55ff0d",
      "&::placeholder": { color: "#8a8aa3" },
      "&:focus": {
        outline: "none",
        backgroundColor: "#fff",
        borderColor: "#6f55ff",
      },
    },
    ".field[ev-valid=false] input": {
      borderColor: "#fa6160",
      color: "#fa6160",
    },
    ".error": {
      left: 0,
      top: "100%",
      color: "#fa6160",
      fontSize: "12px",
      padding: "2px 0 0",
    },
    ".field": {
      position: "relative",
    },
  },
};

function App() {
  const [publicKey, setPublicKey] = useState("pk_live_njLP8FKcMfaFMcByhDGtug");
  const [reference, setReference] = useState(
    Math.floor(Math.random() * 10000000)
  );
  const [descriptor, setDescriptor] = useState("Statement");
  const [firstName, setFirstName] = useState("John");
  const [lastName, setLastName] = useState("Smith");
  const [email, setEmail] = useState("john.smith@example.com");
  const [phone, setPhone] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  const [amount, setAmount] = useState(Math.floor(Math.random() * 1000));
  const [currency, setCurrency] = useState("USD");

  const [events, setEvents] = useState([]);

  const flexecomRef = useRef(null);

  const mountFields = useCallback(() => {
    if (!window.Flexecom) {
      // eslint-disable-next-line no-console
      console.error("window.Flexecom is not defined — ensure Flexecom.js loaded in public/index.html");
      return;
    }

    flexecomRef.current?.unmount();
    setCardComplete(false);

    flexecomRef.current = window.Flexecom.init({
      public_key: publicKey,
      containerId: "card-fields",
      expressContainerId: "express-checkout",
      environment: FLEXECOM_ENVIRONMENT,
      reference: String(reference),
      style: CARD_FIELDS_STYLE,
      billing_address: DEFAULT_BILLING_ADDRESS,
      amount: String(amount),
      currencyCode: currency,
      descriptor: descriptor || null,

      onFieldsUpdated: (state) => setCardComplete(state.complete),

      onLoading: (loading) => setIsLoading(loading),

      onExpressCheckoutReady: () => {
        // eslint-disable-next-line no-console
        console.log("Express checkout ready");
      },
      onExpressCheckoutUnavailable: () => {
        // eslint-disable-next-line no-console
        console.log("Express checkout unavailable — amount/currencyCode missing at init");
      },

      onPaymentSuccess: (result) => {
        setEvents((prev) => [
          ...prev,
          { status: "completed", id: result.id, amount: result.amount, reference: result.reference },
        ]);
        setReference(Math.floor(Math.random() * 10000000))
        setAmount(Math.floor(Math.random() * 1000))
      },
      onPaymentFailure: () => {
        setEvents((prev) => [...prev, { status: "failed" }]);
        setReference(Math.floor(Math.random() * 10000000))
      },
      onError: (error) => {
        alert("Oops: " + error.message);
      },
    });
  }, [publicKey, reference, amount, currency, descriptor]);

  useEffect(() => {
    mountFields();
    return () => flexecomRef.current?.unmount();
  }, [currency]);

  const processPayment = () => {
    setEvents([]);
    flexecomRef.current?.submit({
      amount: String(amount),
      currencyCode: currency,
      descriptor: descriptor || null,
    });
  };

  const renderCards = () => {
    return (
      <>
        <div className="mw-325px">
          <h3>Test Payment Portal</h3>
          <p className="mb-0">
            To test the payment, please fill in the required details on the
            right side
          </p>
        </div>
        <div className="alert alert-light">
          <b>Bogus Gateway</b>
          <table className="table table-borderless text-start fs-6 fw-medium mb-3">
            <tbody>
              <tr>
                <td>Success:</td>
                <td>4242424242424242</td>
              </tr>
              <tr>
                <td>Failure:</td>
                <td>4000000000000002</td>
              </tr>
            </tbody>
          </table>

          <b>Maverick Payments</b>
          <table className="table table-borderless text-start fs-6 fw-medium mb-3">
            <tbody>
              <tr>
                <td>Success:</td>
                <td>4539225011794489</td>
              </tr>
              <tr>
                <td>Failure:</td>
                <td>4000000000000002</td>
              </tr>
            </tbody>
          </table>

          {/*<b>NMI</b>*/}
          {/*<table className="table table-borderless text-start fs-6 fw-medium mb-3">*/}
          {/*  <tbody>*/}
          {/*  <tr>*/}
          {/*    <td>Success:</td>*/}
          {/*    <td>5411111111111115</td>*/}
          {/*  </tr>*/}
          {/*  <tr>*/}
          {/*    <td>Failure:</td>*/}
          {/*    <td>4000120000001154</td>*/}
          {/*  </tr>*/}
          {/*  </tbody>*/}
          {/*</table>*/}

          {/*<b>Global Payments</b>*/}
          {/*<table className="table table-borderless text-start fs-6 fw-medium mb-3">*/}
          {/*  <tbody>*/}
          {/*  <tr>*/}
          {/*    <td>Success:</td>*/}
          {/*    <td>4263970000005262</td>*/}
          {/*  </tr>*/}
          {/*  <tr>*/}
          {/*    <td>Failure:</td>*/}
          {/*    <td>4000120000001154</td>*/}
          {/*  </tr>*/}
          {/*  </tbody>*/}
          {/*</table>*/}

          <b>Stripe</b>
          <table className="table table-borderless text-start fs-6 fw-medium mb-0">
            <tbody>
              <tr>
                <td>Success:</td>
                <td>4242424242424242</td>
              </tr>
              <tr>
                <td>3DS:</td>
                <td>4000002760003184</td>
              </tr>
              <tr>
                <td>Failure:</td>
                <td>4000000000000002</td>
              </tr>
            </tbody>
          </table>
        </div>
      </>
    )
  }

  const renderEvents = () => {
    return events.map((event, i) => {

      if(event.status === "completed"){
        return (
          <div className="alert alert-success mb-0 text-start">
            <h6>Payment Successful                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                      !</h6>
            <div>Status: Success</div>
            <div>Next Action: Notify client of payment success</div>
          </div>
        )
      }else if(event.status === "failed"){
        return (
          <div className="alert alert-danger mb-0 text-start">
            <h6>Payment Failed</h6>
            <div>Status: Failed</div>
            <div>Next Action: None, no applicable processors to retry payment on</div>
          </div>
        )
      }

      return null
    })
  }

  return (
    <div className="page demo">
      <div className="onboarding">
        <div className="onb-aside w-100">
          <div className="t-section">
            <a href="javascript:void(0)">
              <img alt="Logo" src="logo.png" className="h-40px"/>
            </a>
            {(!isLoading && events.length === 0) && renderCards()}
            {renderEvents()}
            {isLoading && (
              <>
                <div className="alert mb-0">
                  <h6>Loading...</h6>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="onb-body">
          <div className={"form"}>
            {/* Starts: Express Checkout */}
            <div className={"s-card"}>
              <label className="form-label">Express Checkout</label>
              <div id={"express-checkout"} />
            </div>
            {/* End: Express Checkout */}

            {/* Starts: Merchant Details */}
            <div className={"s-card"}>
              <label className="form-label">Merchant</label>
              {/* Starts: Merchant Public Key */}
              <div className="form-group">
                <label className="form-label fw-normal">
                  Merchant Public Key*
                </label>
                <input
                  className="form-control"
                  placeholder="Merchant Public Key"
                  value={publicKey}
                  onChange={(e) => setPublicKey(e.target.value || null)}
                  onBlur={mountFields}
                />
                <div className="form-text">
                  Defines the Merchant the transaction is processed on behalf of.
                </div>
              </div>
              {/* end: Merchant Public Key */}
              {/* Starts: Merchant Reference */}
              <div className="form-group">
                <label className="form-label fw-normal">
                  Merchant Reference*
                </label>
                <input
                  className="form-control"
                  placeholder="Merchant Reference"
                  value={reference}
                  onChange={(e) => setReference(e.target.value || null)}
                  onBlur={mountFields}
                />
                <div className="form-text">
                  Merchant's reference for the payment.
                </div>
              </div>
              {/* end: Merchant Reference */}
              {/* Starts: Bank Descriptor */}
              <div className="form-group">
                <label className="form-label fw-normal">
                  Bank Descriptor
                </label>
                <input
                  className="form-control"
                  placeholder="Bank Descriptor"
                  value={descriptor}
                  onChange={(e) => setDescriptor(e.target.value)}
                  onBlur={mountFields}
                  maxLength={18}
                />
                <div className="form-text">
                  Statement descriptor shown to the customer on their bank statement.
                </div>
              </div>
              {/* end: Bank Descriptor */}
              {/* Starts: Amount,Currency  */}
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <label className="form-label fw-normal">Amount*</label>
                    <input
                      className="form-control"
                      placeholder="Amount"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value || null)}
                      onBlur={mountFields}
                    />
                  </div>
                </div>
                <div className="col">
                  <div className="form-group">
                    <label className="form-label fw-normal">Currency*</label>
                    <select
                      className="form-control form-select"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                    >
                      {CURRENCIES.map((code) => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              {/* end: Amount,Currency */}
            </div>
            {/* End: Merchant Details */}

            {/* Starts: Customer Details */}
            <div className={"s-card"}>
              <label className="form-label">Customer Details</label>
              {/* Starts: First and Last Names */}
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <label className="form-label fw-normal">First Name</label>
                    <input
                      className="form-control"
                      placeholder="first_name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value || null)}
                    />
                  </div>
                </div>
                <div className="col">
                  <div className="form-group">
                    <label className="form-label fw-normal">Last Name</label>
                    <input
                      className="form-control"
                      placeholder="last_name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value || null)}
                    />
                  </div>
                </div>
              </div>
              {/* end: First and Last Names */}
              {/* Starts: Email and Phone */}
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <label className="form-label fw-normal">Email</label>
                    <input
                      className="form-control"
                      placeholder="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value || null)}
                    />
                  </div>
                </div>
                <div className="col">
                  <div className="form-group">
                    <label className="form-label fw-normal">Phone</label>
                    <input
                      className="form-control"
                      placeholder="phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value || null)}
                    />
                  </div>
                </div>
              </div>
              {/* end: Email and Phone */}
            </div>
            {/* End: Customer Details */}

            {/* Starts: Card Details */}
            <div className={"s-card"}>
              <label className="form-label">Card Details</label>
              <div id={"card-fields"} />
            </div>
            {/* End: Card Details */}

            <div className="gap"></div>
            <button
              className="btn btn-primary w-100"
              type="submit"
              disabled={isLoading || !cardComplete}
              onClick={processPayment}
            >
              {isLoading ? "Loading..." : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
